import { useEffect, useState } from 'react';
import { reportsApi } from '../api';
import { Users, Target, CheckSquare, DollarSign, TrendingUp, AlertTriangle, Plus, Edit3, Trash2, MessageSquare, ArrowRight, CheckCircle } from 'lucide-react';
import dayjs from 'dayjs';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

export default function DashboardPage() {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await reportsApi.dashboard();
        setData(res.data);
      } catch (e) {
        console.error('Dashboard load error:', e);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, []);

  if (loading) return <div style={{ padding: 40, textAlign: 'center' }}>Загрузка...</div>;
  if (!data) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-danger)' }}>Ошибка загрузки данных</div>;

  const stats = [
    { label: 'Всего лидов',    value: data.leads.total,            icon: Target,       color: '#2563eb' },
    { label: 'Активные сделки', value: data.deals.total,            icon: DollarSign,    color: '#10b981' },
    { label: 'Успешные сделки', value: data.deals.won,              icon: TrendingUp,    color: '#059669' },
    { label: 'Конверсия',       value: `${data.sales.conversionRate}%`, icon: TrendingUp, color: '#7c3aed' },
    { label: 'Сумма сделок',    value: `${Number(data.sales.totalAmount || 0).toLocaleString('ru-RU')} ₽`, icon: DollarSign, color: '#ea580c' },
    { label: 'Задач в работе',  value: data.tasks.pending,          icon: CheckSquare,   color: '#d97706' },
    { label: 'Просрочено задач', value: data.tasks.overdue,        icon: AlertTriangle, color: data.tasks.overdue > 0 ? '#ef4444' : '#64748b' },
    ...(user?.role !== 'MANAGER' ? [{ label: 'Пользователи', value: data.users.total, icon: Users, color: '#0891b2' }] : []),
  ];

  return (
    <div>
      <div className="page-header">
        <h2 className="page-title">Дэшборд</h2>
        <div className="flex gap-2">
          <button className="btn btn-secondary btn-sm">Экспорт</button>
          <button className="btn btn-primary btn-sm">Обновить</button>
        </div>
      </div>

      <div className="dashboard-grid">
        {stats.map((s, i) => (
          <div className="widget" key={i}>
            <div className="widget-icon" style={{ background: `${s.color}15`, color: s.color }}>
              <s.icon size={22} />
            </div>
            <div className="widget-label">{s.label}</div>
            <div className="widget-value">{s.value}</div>
          </div>
        ))}
      </div>

      <div className="card">
        <div className="card-header">
          <div className="card-title">Последние активности</div>
        </div>
        <div className="card-body" style={{ padding: '0 24px' }}>
          {data.recentActivities?.length === 0 && (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--color-text-secondary)' }}>
              Активностей пока нет
            </div>
          )}
          {data.recentActivities?.map((a: any) => {
            const getActivityIcon = (type: string, title: string) => {
              if (type === 'STATUS_CHANGE' && title.startsWith('Задача выполнена')) return { icon: CheckCircle, bg: '#dcfce7', color: '#16a34a' };
              if (type === 'STATUS_CHANGE' && title.includes('WON')) return { icon: CheckCircle, bg: '#dcfce7', color: '#16a34a' };
              switch (type) {
                case 'CREATED': return { icon: Plus, bg: '#dcfce7', color: '#16a34a' };
                case 'UPDATED': return { icon: Edit3, bg: '#dbeafe', color: '#2563eb' };
                case 'DELETED': return { icon: Trash2, bg: '#fee2e2', color: '#dc2626' };
                case 'STATUS_CHANGE': return { icon: TrendingUp, bg: '#fef3c7', color: '#d97706' };
                case 'NOTE': return { icon: MessageSquare, bg: '#f3e8ff', color: '#9333ea' };
                default: return { icon: Target, bg: 'var(--color-primary-soft)', color: 'var(--color-primary)' };
              }
            };

            const getEntityLink = (activity: any) => {
              if (activity.company) return { label: activity.company.name, path: `/companies`, entityId: activity.company.id };
              if (activity.deal) return { label: activity.deal.title, path: `/deals`, entityId: activity.deal.id };
              if (activity.lead) return { label: `${activity.lead.firstName || ''} ${activity.lead.lastName || ''}`.trim(), path: `/leads`, entityId: activity.lead.id };
              if (activity.contact) return { label: `${activity.contact.firstName || ''} ${activity.contact.lastName || ''}`.trim(), path: `/contacts`, entityId: activity.contact.id };
              if (activity.task) return { label: activity.task.title, path: `/tasks`, entityId: activity.task.id };
              return null;
            };

            const actIcon = getActivityIcon(a.type, a.title);
            const entityLink = getEntityLink(a);

            return (
              <div key={a.id} style={{
                display: 'flex', alignItems: 'flex-start', gap: 16,
                padding: '16px 0', borderBottom: '1px solid var(--color-border-soft)'
              }}>
                <div style={{
                  width: 32, height: 32, borderRadius: '50%',
                  background: actIcon.bg,
                  display: 'flex', alignItems: 'center', justifyContent: 'center',
                  color: actIcon.color, flexShrink: 0,
                }}>
                  <actIcon.icon size={14} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 14, fontWeight: 500 }}>{a.title}</div>
                  {a.description && (
                    <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2 }}>
                      {a.description}
                    </div>
                  )}
                  <div style={{ fontSize: 12, color: 'var(--color-text-secondary)', marginTop: 2, display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span>{a.user?.firstName} {a.user?.lastName}</span>
                    <span>•</span>
                    <span>{dayjs(a.date).format('DD MMM YYYY, HH:mm')}</span>
                    {entityLink && (
                      <>
                        <span>•</span>
                        <span
                          style={{ color: 'var(--color-primary)', cursor: 'pointer', display: 'inline-flex', alignItems: 'center', gap: 2 }}
                          onClick={() => navigate(`${entityLink.path}/${entityLink.entityId}`)}
                        >
                          {entityLink.label}
                          <ArrowRight size={10} />
                        </span>
                      </>
                    )}
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}
